import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOrganizationSettingsQuery } from "@/hooks/queries/useOrganizationSettingsQuery";
import { underwritingRuleValue } from "@/services/organizationSettings";
import { resolveBusinessDaysPerMonth, roundOfferFactor } from "@/utils/offerCalculations";

export type LastEditedField = "addedLeverage" | "fundingAmount" | "installment" | null;

export interface OfferValues {
  addedLeverage: number;
  fundingAmount: number;
  installment: number;
  factor: number;
  term: number;
  frequency: string;
}

const FALLBACK_DEFAULTS: OfferValues = {
  addedLeverage: 0,
  fundingAmount: 0,
  installment: 0,
  factor: 1.49,
  term: 60,
  frequency: "daily",
};

const STORAGE_KEY = "offerCalc";

export interface OfferCalcOptions {
  /**
   * False: no localStorage draft, read or written (a chat card, which must not take or replace
   * the dashboard's), and the given initial values are the offer, so the organization's defaults
   * fill only the terms they leave out.
   */
  persist?: boolean;
}

export function usePersistentOfferCalc(
  id: number | undefined,
  initialValues?: Partial<OfferValues>,
  { persist = true }: OfferCalcOptions = {}
): [
  OfferValues,
  React.Dispatch<React.SetStateAction<OfferValues>>,
  LastEditedField,
  React.Dispatch<React.SetStateAction<LastEditedField>>,
  Pick<OfferValues, "factor" | "term" | "frequency"> & { businessDaysPerMonth: number },
] {
  const { data: organizationSettings } = useOrganizationSettingsQuery();
  const offerDefaults = useMemo(
    () => ({
      factor: roundOfferFactor(
        Number(underwritingRuleValue(organizationSettings, "offer_factor") ?? 1.49)
      ),
      term: Number(underwritingRuleValue(organizationSettings, "standard_term_days") ?? 60),
      frequency: String(
        underwritingRuleValue(organizationSettings, "offer_payment_frequency") ?? "daily"
      ),
      businessDaysPerMonth: resolveBusinessDaysPerMonth(
        underwritingRuleValue(organizationSettings, "business_days_per_month")
      ),
    }),
    [organizationSettings]
  );
  const initialStateRef = useRef<{ values: OfferValues; persisted: boolean }>();
  if (!initialStateRef.current) {
    let persisted: OfferValues | undefined;
    try {
      const raw = persist ? localStorage.getItem(STORAGE_KEY) : null;
      if (raw) {
        const stored = JSON.parse(raw) as { id?: number; values?: OfferValues };
        if (stored.id === id && stored.values) {
          persisted = { ...stored.values, factor: roundOfferFactor(stored.values.factor) };
        }
      }
    } catch {
      // Invalid local storage falls back to org defaults below.
    }
    const seeded = persisted ?? {
      ...FALLBACK_DEFAULTS,
      factor: offerDefaults.factor,
      term: offerDefaults.term,
      frequency: offerDefaults.frequency,
      ...initialValues,
    };
    initialStateRef.current = {
      values: { ...seeded, factor: roundOfferFactor(seeded.factor) },
      persisted: persisted !== undefined,
    };
  }

  const [values, setValuesRaw] = useState<OfferValues>(() => {
    return initialStateRef.current!.values;
  });
  const orgDefaultsAppliedRef = useRef(initialStateRef.current.persisted);
  const offerTermsTouchedRef = useRef(
    persist
      ? { factor: false, term: false, frequency: false }
      : {
          factor: initialValues?.factor != null,
          term: initialValues?.term != null,
          frequency: initialValues?.frequency != null,
        }
  );

  useEffect(() => {
    if (!organizationSettings || orgDefaultsAppliedRef.current) return;
    orgDefaultsAppliedRef.current = true;
    const touched = offerTermsTouchedRef.current;
    setValuesRaw((previous) => ({
      ...previous,
      factor: touched.factor ? previous.factor : offerDefaults.factor,
      term: touched.term ? previous.term : offerDefaults.term,
      frequency: touched.frequency ? previous.frequency : offerDefaults.frequency,
    }));
  }, [offerDefaults, organizationSettings]);

  const [lastEdited, setLastEditedRaw] = useState<LastEditedField>(() => {
    try {
      const raw = persist ? localStorage.getItem(STORAGE_KEY) : null;
      if (raw) {
        const stored = JSON.parse(raw) as any;
        if (stored.id === id) return stored.lastEdited ?? null;
      }
    } catch {}
    return null;
  });

  // The setters are stable: OfferCalculator's recalculation effect depends on them, and a new
  // setter each render ran it, and set new values, on every render once a field was typed.
  // The draft is written after the render, so it holds the values and last-typed field together.
  const idRef = useRef(id);
  idRef.current = id;
  const typedForIdRef = useRef<{ id: number | undefined } | null>(null);

  useEffect(() => {
    if (!persist || !typedForIdRef.current || typedForIdRef.current.id !== id) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ id, values, lastEdited }));
  }, [persist, id, values, lastEdited]);

  const setValues = useCallback<React.Dispatch<React.SetStateAction<OfferValues>>>((action) => {
    typedForIdRef.current = { id: idRef.current };
    setValuesRaw((prev) => {
      const next = typeof action === "function" ? action(prev) : action;
      if (next.factor !== prev.factor) offerTermsTouchedRef.current.factor = true;
      if (next.term !== prev.term) offerTermsTouchedRef.current.term = true;
      if (next.frequency !== prev.frequency) offerTermsTouchedRef.current.frequency = true;
      return next;
    });
  }, []);

  const setLastEdited = useCallback<React.Dispatch<React.SetStateAction<LastEditedField>>>(
    (action) => {
      typedForIdRef.current = { id: idRef.current };
      setLastEditedRaw(action);
    },
    []
  );

  return [values, setValues, lastEdited, setLastEdited, offerDefaults];
}
